<div class="my-project-content">
  <!-- Header Section with Tabs and Credits -->
  <div class="my-project-header-with-tabs">
    <!-- Tabs Navigation -->
    <div class="my-project-tabs">
    <button 
      class="my-project-tab"
      [class.active]="activeTab === 'list'"
      (click)="setActiveTab('list')"
      type="button"
    >
      Notebook List
    </button>
    <button 
      class="my-project-tab"
      [class.active]="activeTab === 'create'"
      (click)="setActiveTab('create')"
      type="button"
    >
      Create Notebook
    </button>
    <button 
      class="my-project-tab"
      [class.active]="activeTab === 'request-credits'"
      (click)="setActiveTab('request-credits')"
      type="button"
    >
      Request Credits
    </button>
  </div>

    <!-- Credits Card (Right Aligned) -->
    <div class="credits-card">
      <!-- Skeleton Loader -->
      <ng-container *ngIf="creditsLoading">
        <div class="credits-icon skeleton-icon">
          <div class="skeleton-circle"></div>
        </div>
        <div class="credits-content">
          <div class="skeleton-text skeleton-label"></div>
          <div class="skeleton-text skeleton-value"></div>
          <div class="skeleton-text skeleton-expiry"></div>
        </div>
      </ng-container>

      <!-- Actual Content -->
      <ng-container *ngIf="!creditsLoading">
        <div class="credits-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1 10H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="7" cy="15" r="1" fill="currentColor"/>
            <circle cx="10" cy="15" r="1" fill="currentColor"/>
            <circle cx="13" cy="15" r="1" fill="currentColor"/>
          </svg>
        </div>
        <div class="credits-content">
          <span class="credits-label">Credits Remaining</span>
          <span class="credits-value" [class.expired]="isCreditsExpired()">{{ creditsRemaining | number }}</span>
          <span class="credits-expiry" *ngIf="creditsExpirationDate" [class.expired]="isCreditsExpired()">
            <span *ngIf="creditsExpirationDate === 'NA'">Expires at: NA</span>
            <span *ngIf="creditsExpirationDate !== 'NA'">Expires: {{ creditsExpirationDate | date:'MMM dd, yyyy' }}</span>
          </span>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Tab Content: Create Notebook -->
  <div class="my-project-tab-content" *ngIf="activeTab === 'create'">
    <div class="create-notebook-content">
      <form [formGroup]="notebookForm" class="notebook-form">
        <!-- Name Field -->
        <div class="form-field">
          <label for="notebookName" class="form-label">
            Name <span class="required">*</span>
          </label>
          <input
            type="text"
            id="notebookName"
            formControlName="name"
            class="form-input"
            [class.input-error]="notebookForm.get('name')?.invalid && notebookForm.get('name')?.touched"
            placeholder="Enter notebook name"
            autocomplete="off"
          />
          <div 
            *ngIf="notebookForm.get('name')?.invalid && notebookForm.get('name')?.touched"
            class="error-message"
          >
            {{ getNotebookNameErrors() }}
          </div>
          
          <!-- Validation Rules Tooltip -->
          <div class="validation-rules" *ngIf="notebookForm.get('name')?.value || notebookForm.get('name')?.touched">
            <div class="validation-rules-title">Validation Rules:</div>
            <ul class="validation-rules-list">
              <li [class.rule-valid]="isLowercaseOnlyValid()">
                <svg *ngIf="isLowercaseOnlyValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Only lowercase letters are accepted
              </li>
              <li [class.rule-valid]="isStartsWithLetterOrNumberValid()">
                <svg *ngIf="isStartsWithLetterOrNumberValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Must start with a letter or number
              </li>
              <li [class.rule-valid]="isHyphenValid()">
                <svg *ngIf="isHyphenValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Hyphen allowed inside segments
              </li>
            </ul>
          </div>
        </div>

        <!-- Instance Type Field -->
        <div class="form-field">
          <label class="form-label">
            Instance Type <span class="required">*</span>
          </label>
          <div class="instance-type-options">
            <div
              *ngFor="let option of instanceTypeOptions"
              class="instance-type-option-wrapper"
            >
              <div
                class="instance-type-option"
                [class.selected]="notebookForm.get('instanceType')?.value === option.value"
                [class.disabled]="option.value === 'GPU' && isGpuDisabled()"
                (click)="selectInstanceType(option.value)"
              >
                <div class="instance-type-option-content">
                  <div class="instance-type-radio">
                    <div class="radio-circle" [class.checked]="notebookForm.get('instanceType')?.value === option.value">
                      <svg *ngIf="notebookForm.get('instanceType')?.value === option.value" width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="5" fill="currentColor"/>
                      </svg>
                    </div>
                  </div>
                  <div class="instance-type-label">{{ option.label }}</div>
                </div>
              </div>
              <!-- GPU Warning Message: show until roles array has Compute (active, not just pending) -->
              <div 
                *ngIf="option.value === 'GPU' && !hasActiveComputeRole" 
                class="gpu-warning-message"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span *ngIf="hasPendingComputeRole()">
                  Your Compute Role upgrade request is currently under review. Please wait for <span class="approval-highlight">Approval</span>.
                </span>
                <span *ngIf="!hasPendingComputeRole()">
                  You must have Compute Access in order to make GPU Request.
                </span>
              </div>
            </div>
          </div>
          <div 
            *ngIf="notebookForm.get('instanceType')?.invalid && notebookForm.get('instanceType')?.touched"
            class="error-message"
          >
            Instance type is required
          </div>
        </div>

        <!-- CPU Info Field (Only shown when CPU is selected) -->
        <div class="form-field" *ngIf="notebookForm.get('instanceType')?.value === 'CPU'">
          <div class="cpu-info-card">
            <div class="cpu-info-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
                <rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor"/>
                <path d="M4 10H2M22 10H20M4 14H2M22 14H20M10 4V2M14 4V2M10 22V20M14 22V20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <h3 class="cpu-info-title">CPU Notebook</h3>
              <div class="cpu-info-details-inline">
                <div class="cpu-info-item-inline">
                  <span class="cpu-info-label">Memory:</span>
                  <span class="cpu-info-value">1 <span class="cpu-info-unit">GB</span></span>
                </div>
                <div class="cpu-info-item-inline">
                  <span class="cpu-info-label">Disk:</span>
                  <span class="cpu-info-value">10 <span class="cpu-info-unit">GB</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- GPU Info Field (Only shown when GPU is selected) -->
        <div class="form-field" *ngIf="notebookForm.get('instanceType')?.value === 'GPU'">
          <div class="cpu-info-card">
            <div class="cpu-info-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: #ff8c00;">
                <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2" />
                <rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor" />
                <path d="M4 10H2M22 10H20M4 14H2M22 14H20M10 4V2M14 4V2M10 22V20M14 22V20" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
              <h3 class="cpu-info-title">GPU Notebook</h3>
              <div class="cpu-info-details-inline">
                <div class="cpu-info-item-inline">
                  <span class="cpu-info-label">Memory:</span>
                  <span class="cpu-info-value">6 <span class="cpu-info-unit">GB</span></span>
                </div>
                <div class="cpu-info-item-inline">
                  <span class="cpu-info-label">Disk:</span>
                  <span class="cpu-info-value">65 <span class="cpu-info-unit">GB</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Policy Agreement Checkbox -->
        <div class="policy-agreement-field">
          <label class="policy-checkbox-label">
            <input
              type="checkbox"
              class="policy-checkbox checkbox-checkmark-custom"
              formControlName="policyAgreed"
              [class.error]="notebookForm.get('policyAgreed')?.invalid && notebookForm.get('policyAgreed')?.touched"
            />
            <span class="policy-checkbox-text">
              I agree to the 
              <a href="#" class="policy-link" (click)="openPolicyModal(); $event.preventDefault()">Policy terms and rules</a>
            </span>
          </label>
          <div 
            *ngIf="notebookForm.get('policyAgreed')?.invalid && notebookForm.get('policyAgreed')?.touched"
            class="error-message"
          >
            You must agree to the policy terms and rules
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button 
            type="button"
            class="btn-submit"
            (click)="onSubmitNotebook()"
            [disabled]="!notebookForm.valid"
          >
            Create Notebook
          </button>
          <button 
            type="button"
            class="btn-cancel"
            (click)="setActiveTab('list')"
          >
            Cancel
          </button>
        </div>
    </form>
  </div>
  </div>

  <!-- Tab Content: Request Credits -->
  <div class="my-project-tab-content" *ngIf="activeTab === 'request-credits'">
    <!-- Message when user doesn't have Compute role -->
    <div class="compute-role-message" *ngIf="!hasComputeRole">
      <div class="message-container">
        <svg class="message-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="message-content">
          <h3 class="message-title">Compute Role Required</h3>
          <p class="message-text">You need to have Compute role in order to access Request Credits.</p>
          <button 
            class="upgrade-role-btn"
            (click)="openUpgradeRoleModal()"
            type="button"
          >
            Upgrade Your Role
          </button>
        </div>
      </div>
    </div>
    
    <!-- Message when Compute role is only pending (not yet approved) - do not show request form -->
    <div class="compute-role-message" *ngIf="hasComputeRole && !hasActiveComputeRole">
      <div class="message-container">
        <svg class="message-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="message-content">
          <h3 class="message-title">Compute Role Pending</h3>
          <p class="message-text">Your Compute role request is pending approval. You can request credits once it is approved.</p>
        </div>
      </div>
    </div>
    
    <!-- Pending credit request warning (same as Join organisation / data-provider-entity pending request info) -->
    <div *ngIf="hasActiveComputeRole && hasPendingCreditRequest && !pendingCreditRequestLoading" class="pending-request-info-wrapper">
      <div class="pending-request-info">
        <div class="pending-request-info--icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="pending-request-info--text">
          <strong>Pending Request.</strong> You already have a pending credit request.<br>
          Please wait for approval before submitting another.
        </div>
        <button
          type="button"
          class="btn-withdraw-entity"
          [disabled]="withdrawCreditLoading"
          (click)="openWithdrawCreditModal()"
        >
          {{ withdrawCreditLoading ? 'Withdrawing...' : 'Withdraw Request' }}
        </button>
      </div>
    </div>
    
    <!-- Load Request credits: open modal with all requests (granted, rejected, pending) when there are no pending requests -->
    <div *ngIf="hasActiveComputeRole && !hasPendingCreditRequest && !pendingCreditRequestLoading" class="request-credits-history-row">
      <button
        type="button"
        class="btn-load-request-credits"
        (click)="openRequestCreditsHistoryModal()"
      >
      Browse Request History
      </button>
    </div>
    
    <!-- Request Credits Form (only show when user has active Compute role, not just pending). Prepopulate with pending request data when available. -->
    <app-request-credits
      *ngIf="hasActiveComputeRole"
      [hasPendingRequest]="hasPendingCreditRequest"
      [initialData]="pendingCreditRequestData"
      (requestSubmitted)="onCreditRequestSubmitted()"
    ></app-request-credits>
  </div>

  <!-- Tab Content: Notebook List -->
  <div class="my-project-tab-content" *ngIf="activeTab === 'list'">
  <!-- Templates defined outside app-table for ViewChild access -->
  <ng-template #expandCell let-row>
    <button 
      class="expand-btn"
      (click)="toggleRowExpansion(row)"
      [attr.aria-expanded]="isRowExpanded(row)"
      type="button"
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        [style.transform]="isRowExpanded(row) ? 'rotate(90deg)' : 'rotate(0deg)'"
        [style.transition]="'transform 0.2s ease'"
      >
        <path 
          d="M9 18L15 12L9 6" 
          stroke="currentColor" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </ng-template>

  <ng-template #childRow let-row>
    <tr class="table--child-row">
      <td [attr.colspan]="columns.length" class="table--child-cell">
        <div class="child-row-content">
          <div class="child-row-grid">
            <div class="child-row-item">
              <span class="child-row-label">CPU Request</span>
              <span class="child-row-value">{{ row.cpuRequest || 'N/A' }}</span>
            </div>
            <div class="child-row-item">
              <span class="child-row-label">CPU Limit</span>
              <span class="child-row-value">{{ row.cpuLimit || 'N/A' }}</span>
            </div>
            <div class="child-row-item">
              <span class="child-row-label">Memory Request</span>
              <span class="child-row-value">{{ row.memoryRequest || 'N/A' }}</span>
            </div>
            <div class="child-row-item">
              <span class="child-row-label">Memory Limit</span>
              <span class="child-row-value">{{ row.memoryLimit || 'N/A' }}</span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  </ng-template>

  <div class="my-project-table-wrapper">
    <app-table
      [columns]="columns"
      [data]="projectsData"
      [scrollHeight]="'600px'"
      [loading]="loading"
      [pagination]="pagination"
      [emptyMessage]="emptyMessage"
      [striped]="true"
      [bordered]="true"
      [hoverable]="true"
      [childRowTemplate]="childRowTemplate"
      [isRowExpanded]="getIsRowExpanded()"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)"
      (sortChange)="onSortChange($event)"
    >
      <!-- Custom Type Column (CPU / GPU chips) -->
      <ng-template #typeCell let-row>
        <div class="type-chip" [ngClass]="getTypeChipClass(row.type)">
          {{ row.type || '—' }}
        </div>
      </ng-template>

      <!-- Custom Status Column -->
      <ng-template #statusCell let-row>
        <div class="status-chip" [ngClass]="getStatusClass(row.status)">
          <span *ngIf="!isOpeningStatus(row.status)" class="status-text">{{ row.status }}</span>
          <div *ngIf="isOpeningStatus(row.status)" class="status-with-loader">
            <div class="status-loader">
              <div class="spinner-dots">
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
                <div class="spinner-dot"></div>
              </div>
            </div>
            <span class="status-text">{{ row.status }}</span>
          </div>
        </div>
      </ng-template>

      <!-- Custom Date Column -->
      <ng-template #dateCell let-row>
        <span>{{ formatDate(row.createdAt) }}</span>
      </ng-template>

      <!-- Custom Action Column -->
      <ng-template #actionCell let-row>
        <div class="action-cell-wrapper">
          <button 
            class="action-btn" 
            appActionDropdownTrigger
            (click)="toggleActionDropdown($event, row)"
            [class.active]="isDropdownOpen(row)"
            type="button"
          >
            <span>Action</span>
          </button>
          
          <!-- Dropdown Menu -->
          <div 
            class="action-dropdown-menu" 
            *ngIf="isDropdownOpen(row)" 
            [attr.data-dropdown-id]="row.notebookName || row.id"
            (click)="$event.stopPropagation()"
          >
            <ul class="dropdown-list">
              <!-- Running Status: Show Open and Stop -->
              <li class="dropdown-item" *ngIf="isRunningStatus(row.status)">
                <button class="dropdown-link" (click)="handleAction('open', row)">
                  Open
                </button>
              </li>
              <li class="dropdown-item" *ngIf="isRunningStatus(row.status)">
                <button class="dropdown-link" (click)="handleAction('stop', row)">
                  Stop
                </button>
              </li>
              
              <!-- Stopped Status: Show Start and Delete -->
              <li class="dropdown-item" *ngIf="isStoppedStatus(row.status)">
                <button class="dropdown-link" (click)="handleAction('start', row)">
                  Start
                </button>
              </li>
              <li class="dropdown-item" *ngIf="isStoppedStatus(row.status)">
                <button class="dropdown-link dropdown-link--danger" (click)="handleAction('delete', row)">
                  Delete
                </button>
              </li>
              
              <!-- Opening Status: Show Delete -->
              <li class="dropdown-item" *ngIf="isOpeningStatus(row.status)">
                <button class="dropdown-link dropdown-link--danger" (click)="handleAction('delete', row)">
                  Delete
                </button>
              </li>
            </ul>
          </div>
        </div>
      </ng-template>
    </app-table>
  </div>
</div>

<!-- Create Notebook Modal -->
<app-modal
  [(visible)]="showCreateNotebookModal"
  header="Notebook Information"
  [width]="'600px'"
  [showFooter]="true"
  [confirmLabel]="'Submit'"
  [cancelLabel]="'Cancel'"
  [confirmButtonDisabled]="!notebookForm.valid"
  (onConfirm)="onSubmitNotebook()"
  (onCancel)="onModalCancel()"
  (onClose)="onModalClose()"
>
  <div class="create-notebook-modal-content">
    <form [formGroup]="notebookForm" class="notebook-form">
      <!-- Name Field -->
      <div class="form-field">
        <label for="notebookName" class="form-label">
          Name <span class="required">*</span>
        </label>
        <input
          type="text"
          id="notebookName"
          formControlName="name"
          class="form-input"
          [class.input-error]="notebookForm.get('name')?.invalid && notebookForm.get('name')?.touched"
          placeholder="Enter notebook name"
          autocomplete="off"
        />
        <div 
          *ngIf="notebookForm.get('name')?.invalid && notebookForm.get('name')?.touched"
          class="error-message"
        >
          {{ getNotebookNameErrors() }}
        </div>
        
        <!-- Validation Rules Tooltip -->
        <div class="validation-rules" *ngIf="notebookForm.get('name')?.value || notebookForm.get('name')?.touched">
          <div class="validation-rules-title">Validation Rules:</div>
          <ul class="validation-rules-list">
            <li [class.rule-valid]="isLowercaseOnlyValid()">
              <svg *ngIf="isLowercaseOnlyValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Only lowercase letters are accepted
            </li>
            <li [class.rule-valid]="isStartsWithLetterOrNumberValid()">
              <svg *ngIf="isStartsWithLetterOrNumberValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Must start with a letter or number
            </li>
            <li [class.rule-valid]="isHyphenValid()">
              <svg *ngIf="isHyphenValid()" class="check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Hyphen allowed inside segments
            </li>
          </ul>
        </div>
      </div>

      <!-- Instance Type Field -->
      <div class="form-field">
        <label class="form-label">
          Instance Type <span class="required">*</span>
        </label>
        <div class="instance-type-options">
          <div
            *ngFor="let option of instanceTypeOptions"
            class="instance-type-option"
            [class.selected]="notebookForm.get('instanceType')?.value === option.value"
            (click)="selectInstanceType(option.value)"
          >
            <div class="instance-type-option-content">
              <div class="instance-type-radio">
                <div class="radio-circle" [class.checked]="notebookForm.get('instanceType')?.value === option.value">
                  <svg *ngIf="notebookForm.get('instanceType')?.value === option.value" width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="5" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div class="instance-type-label">{{ option.label }}</div>
            </div>
          </div>
        </div>
        <div 
          *ngIf="notebookForm.get('instanceType')?.invalid && notebookForm.get('instanceType')?.touched"
          class="error-message"
        >
          Instance type is required
        </div>
      </div>

      <!-- CPU Info Field (Only shown when CPU is selected) -->
      <div class="form-field" *ngIf="notebookForm.get('instanceType')?.value === 'CPU'">
        <div class="cpu-info-card">
          <div class="cpu-info-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
              <rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor"/>
              <path d="M4 10H2M22 10H20M4 14H2M22 14H20M10 4V2M14 4V2M10 22V20M14 22V20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h3 class="cpu-info-title">CPU Notebook</h3>
          </div>
          <div class="cpu-info-details">
            <div class="cpu-info-item">
              <span class="cpu-info-label">Memory</span>
              <span class="cpu-info-value">1 <span class="cpu-info-unit">GB</span></span>
            </div>
            <div class="cpu-info-item">
              <span class="cpu-info-label">Disk</span>
              <span class="cpu-info-value">10 <span class="cpu-info-unit">GB</span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Note Message -->
      <div class="note-message">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>It might take 1-2 minutes to open notebook, please wait</span>
      </div>
    </form>

  </div>
</app-modal>

  <!-- Policy Terms Modal -->
  <app-modal
    [(visible)]="showPolicyModal"
    header="Policy Terms and Rules"
    [width]="'800px'"
    [showFooter]="false"
    (visibleChange)="showPolicyModal = $event"
  >
    <div class="policy-modal-content">
      <div class="policy-content">
        <h3>1. Policy Guidelines</h3>
        <p>This usage policy outlines the guidelines for utilizing the MahaAgX Sandbox environment. Designed for flexibility and high-performance data science, this policy ensures equitable resource distribution and data security for all users.</p>

        <h4>1.1. Access & Authentication</h4>
        <ul>
          <li><strong>Credit-Based Model:</strong> Access to GPU resources is governed by a credit system.</li>
          <li><strong>Credit Allocation:</strong> Credits are not granted automatically. They will be allocated to users following an evaluation of usage requirements, which must be submitted via the official Credit Request Form.</li>
          <li><strong>Compute Role Approval:</strong> To access and launch any Notebook environment, a user must have a Compute Role explicitly approved by the administrator.</li>
          <li><strong>On-Demand GPU Availability:</strong> Once credits are allocated, GPU-enabled notebooks (if available) can be accessed immediately without prior booking.</li>
          <li><strong>Credit-Free CPU Access:</strong> Standard CPU Notebooks are credit-free and may be used for general development tasks, subject to hardware availability.</li>
        </ul>

        <h4>1.2. Resource Allocation & GPU Selection</h4>
        <ul>
          <li><strong>Choice of Hardware:</strong> Users may select from various GPU configuration options based on the complexity of their models.</li>
          <li><strong>Fair Use:</strong> MahaAgX reserves the right to manage concurrent sessions to ensure system stability for the entire user community.</li>
        </ul>

        <h4>1.3. Data Retention & Management</h4>
        <ul>
          <li><strong>Persistent Storage:</strong> Work is not deleted at the end of a session. Data is retained in the environment as long as the user does not manually delete the Notebook.</li>
          <li><strong>User Responsibility:</strong> Users are responsible for managing their storage and deleting unused notebooks to free up system resources.</li>
        </ul>

        <h4>1.4. API & Integration Policy</h4>
        <ul>
          <li><strong>Custom Playbooks:</strong> Users have the flexibility to use Custom Playbooks for API-based data access directly within the IDE, unlike more restrictive environments.</li>
          <li><strong>Security:</strong> Users must follow best practices for credential management, ensuring API keys are not exposed in shared code.</li>
        </ul>

        <h4>1.5. Prohibited Activities</h4>
        <p>Users are strictly prohibited from using Sandbox resources for:</p>
        <ul>
          <li>Cryptocurrency mining or non-research-related compute tasks.</li>
          <li>Unauthorized network scanning or security testing.</li>
          <li>Circumventing the credit-request evaluation process.</li>
        </ul>

        <h4>1.6. Support & Maintenance</h4>
        <ul>
          <li><strong>Idle Termination:</strong> To preserve your allocated credits, sessions that remain idle for an extended period may be automatically terminated.</li>
          <li><strong>System Updates:</strong> Periodic maintenance windows will be communicated via the platform dashboard.</li>
        </ul>

        <h3>2. Rules and Limitations</h3>

        <h4>2.1. Compute & Resource Usage</h4>
        <ul>
          <li><strong>Idle Resource Policy:</strong> While CPU notebooks are credit-free, leaving idle sessions running is prohibited. Active kernels with no computational activity for more than 60 minutes may be automatically stopped to free up allocation for other users.</li>
          <li><strong>Commercial Use Restriction:</strong> The Sandbox is for research, development, and prototyping. Running production-grade high-traffic applications or commercial APIs directly from the Sandbox without a commercial agreement is not permitted.</li>
        </ul>

        <h4>2.2. Credit & Role Governance</h4>
        <ul>
          <li><strong>Credit Transparency:</strong> Credits allocated via the Credit Request Form are strictly for the project described. Using research credits for unrelated personal projects or unauthorized external consulting is a violation of the terms.</li>
          <li><strong>Role-Based Constraints:</strong> Access is tied to the Compute Role. If a user's role status changes or expires, access to existing notebooks and data may be locked until re-approval.</li>
          <li><strong>Request Accuracy:</strong> Users must provide realistic estimations in their credit request form. Consistent over-estimation or intentional under-utilization of requested high-end GPUs may lead to lower priority in future allocations.</li>
        </ul>

        <h4>2.3. Data & Persistence Limitations</h4>
        <ul>
          <li><strong>Storage Quotas:</strong> Although data is retained until deleted by the user, each notebook environment has a soft storage limit (e.g., 50GB). Exceeding this limit will prevent new data from being saved until space is cleared.</li>
          <li><strong>Persistence Integrity:</strong> While MahaAgX retains your notebooks, this is not a permanent backup service. MahaAgX is not liable for data loss due to system-wide upgrades or hardware failure. Users should export critical code to external repositories (e.g., GitHub/GitLab).</li>
          <li><strong>Data Privacy:</strong> Users must not store or process sensitive Personal Identifiable Information (PII) or highly classified data without explicit security clearance and encryption.</li>
        </ul>

        <h4>2.4. Prohibited Technical Actions</h4>
        <ul>
          <li><strong>Cryptomining:</strong> Any form of cryptocurrency mining is strictly prohibited and will result in immediate and permanent account termination.</li>
          <li><strong>Network Probing:</strong> Using the Sandbox for port scanning, IP spoofing, or launching network-based attacks (DDoS) is strictly forbidden.</li>
          <li><strong>Software Piracy:</strong> Running or storing "cracked" software or unlicensed commercial libraries within the IDE is prohibited.</li>
        </ul>
      </div>
    </div>
  </app-modal>

  <!-- Navigation Guard Modal -->
  <app-modal
    [(visible)]="showNavigationGuardModal"
    header="Notebooks Are Opening"
    [width]="'500px'"
    [showFooter]="false"
    (visibleChange)="showNavigationGuardModal = $event"
  >
    <div class="navigation-guard-modal-content">
      <div class="navigation-guard-message">
        <p>You have <strong>{{ openingNotebooks.length }}</strong> notebook(s) that are currently opening:</p>
        <ul class="opening-notebooks-list">
          <li *ngFor="let notebook of openingNotebooks">
            <strong>{{ notebook.notebookName }}</strong>
          </li>
        </ul>
        <p>Please wait until the Notebook is opened or Delete the notebook before navigating away.</p>
      </div>
      
      <div class="navigation-guard-actions">
        <button 
          class="navigation-guard-btn navigation-guard-btn--wait"
          (click)="handleWaitForNotebooksNavigation()"
          type="button"
        >
          Continue to wait
        </button>
        <button 
          class="navigation-guard-btn navigation-guard-btn--delete"
          (click)="handleDeleteOpeningNotebooksNavigation()"
          type="button"
        >
          Delete Notebook
        </button>
      </div>
    </div>
  </app-modal>

  <!-- Withdraw Credit Request Confirmation (replica of Join org withdraw modal) -->
  <app-confirmation-dialog
    [(visible)]="showWithdrawCreditModal"
    type="warning"
    title="Withdraw Request"
    message="Are you sure you want to withdraw your pending credit request? You can submit a new request after withdrawing."
    confirmLabel="Withdraw"
    cancelLabel="Cancel"
    themeColor="orange"
    width="480px"
    [loading]="withdrawCreditLoading"
    (onConfirm)="confirmWithdrawCreditRequest()"
    (onCancel)="closeWithdrawCreditModal()"
    (onClose)="closeWithdrawCreditModal()"
  ></app-confirmation-dialog>

  <!-- Notebook Ready Confirmation Modal -->
  <app-modal
    [(visible)]="showNotebookReadyModal"
    header="Notebook is Ready"
    [width]="'500px'"
    [showFooter]="true"
    [confirmLabel]="'OK'"
    [cancelLabel]="'Cancel'"
    (onConfirm)="onProceedToNotebook()"
    (onCancel)="closeNotebookReadyModal()"
    (onClose)="closeNotebookReadyModal()"
  >
    <div class="notebook-ready-modal-content">
      <div class="notebook-ready-message">
        <p>Your notebook <strong>{{ readyNotebook?.notebookName }}</strong> is now <strong>Running</strong> and ready to use.</p>
        <p>Click <strong>OK</strong> to open it in a new page.</p>
      </div>
    </div>
  </app-modal>

  <!-- Request Credits History Modal: granted, rejected, pending -->
  <app-modal
    [(visible)]="showRequestCreditsHistoryModal"
    header="Request Credits History"
    [width]="'900px'"
    [showFooter]="false"
    (onClose)="closeRequestCreditsHistoryModal()"
  >
    <div class="request-credits-history-modal-content">
      <div *ngIf="creditRequestHistoryLoading" class="history-loading">Loading...</div>
      <div *ngIf="!creditRequestHistoryLoading && creditRequestHistory.length === 0" class="history-empty">No request credits history found.</div>
      <div *ngIf="!creditRequestHistoryLoading && creditRequestHistory.length > 0" class="history-table-wrapper">
        <table class="history-table">
          <thead>
            <tr>
              <th>Requested On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of creditRequestHistory; let i = index" [class.dropdown-open]="openHistoryActionIndex === i">
              <td>{{ row.requestedOn }}</td>
              <td><span [ngClass]="getStatusChipClass(row.status)">{{ row.status }}</span></td>
              <td class="action-cell">
                <button 
                  class="action-btn" 
                  (click)="toggleHistoryActionDropdown($event, i)"
                  type="button"
                >
                  Action
                </button>
                <div class="action-dropdown" *ngIf="openHistoryActionIndex === i" (click)="$event.stopPropagation()">
                  <button class="action-dropdown-item" (click)="fillFromHistory(row)" type="button">
                    Fill
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </app-modal>
